import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AlgorithmOption {
    value: string;
    label: string;
}

interface HashAlgorithmSelectorProps {
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
}

interface AlgorithmWarning {
    value: string;
    message: string;
}

function createAlgorithmSelector(algorithms: AlgorithmOption[], defaultValue: string, warnings?: AlgorithmWarning[]) {
    const warningMap = new Map(warnings?.map(warning => [warning.value, warning.message]));

    const Component: React.FC<HashAlgorithmSelectorProps> = ({ nodeState, setNodeState }) => {
        const algorithm = (nodeState.algorithm as string) || defaultValue;
        const warningMessage = warningMap.get(algorithm);

        return (
            <div className="px-3 py-2">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] opacity-50 shrink-0">Algorithm:</span>
                    <Select value={algorithm} onValueChange={value => setNodeState('algorithm', value)}>
                        <SelectTrigger className="h-6 w-auto min-w-[100px] px-2 py-0.5 text-[10px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {algorithms.map(algo => (
                                <SelectItem key={algo.value} value={algo.value}>
                                    {algo.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {warningMessage && (
                    <p className="mt-1.5 max-w-[200px] rounded bg-amber-500/15 px-2 py-1 text-[10px] leading-snug text-amber-700 dark:text-amber-500">
                        {warningMessage}
                    </p>
                )}
            </div>
        );
    };
    return Component;
}

export const CheckPasswordAlgorithmSelector = createAlgorithmSelector(
    [
        { value: 'argon2', label: 'Argon2' },
        { value: 'sha256', label: 'SHA-256' },
        { value: 'sha512', label: 'SHA-512' },
        { value: 'plain', label: 'Plain Text' },
    ],
    'argon2',
    [
        {
            value: 'plain',
            message:
                'Please beware that plain text password checking is bad practice because it requires storage of plain text passwords for comparison.',
        },
    ],
);

export const HashPasswordAlgorithmSelector = createAlgorithmSelector(
    [
        { value: 'argon2', label: 'Argon2' },
        { value: 'sha256', label: 'SHA-256' },
        { value: 'sha512', label: 'SHA-512' },
    ],
    'argon2',
);
